import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { getAllUsers, updateUserDetails, deleteUser } from "../../services/userService";
import { toast } from "react-hot-toast";
import { Users, Search, Eye, Edit, Activity, X, Save, Filter, ChevronDown, Trash2, Pencil } from "lucide-react";

export default function UsersList() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterReferredBy, setFilterReferredBy] = useState("");
  const [filterSponsoredBy, setFilterSponsoredBy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDailyRecordModal, setShowDailyRecordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [dailyRecordForm, setDailyRecordForm] = useState({
    date: new Date().toISOString().split("T")[0],
    weight: "",
    fat: "",
    muscles: "",
    bmi: "",
    bmr: "",
    bodyAge: "",
    visceralFat: "",
  });
  console.log(users, "users")
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShakesModal, setShowShakesModal] = useState(false);
  const [shakeEditUser, setShakeEditUser] = useState(null);
  const [shakeEditValue, setShakeEditValue] = useState("");
  const [savingShakes, setSavingShakes] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers();
      setUsers(usersData);
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenShakesModal = (user) => {
    setShakeEditUser(user);
    setShakeEditValue(String(user.inventory?.remainingShakes ?? ""));
    setShowShakesModal(true);
  };

  const handleCloseShakesModal = () => {
    setShowShakesModal(false);
    setShakeEditUser(null);
    setShakeEditValue("");
  };

  const handleSaveShakes = async () => {
    const qty = parseInt(shakeEditValue, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    try {
      setSavingShakes(true);
      await updateUserDetails(shakeEditUser.id, { "inventory.remainingShakes": qty });
      toast.success("Remaining shakes updated!");
      handleCloseShakesModal();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update shakes");
      console.error(error);
    } finally {
      setSavingShakes(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "superadmin":
        return "bg-red-100 text-red-700";
      case "admin":
        return "bg-orange-100 text-orange-700";
      case "user":
        return "bg-green-100 text-green-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "superadmin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "user":
        return "User";
      default:
        return role || "User";
    }
  };

  const handleOpenDailyRecordModal = (user) => {
    setSelectedUser(user);
    setShowDailyRecordModal(true);
    setDailyRecordForm({
      date: new Date().toISOString().split("T")[0],
      weight: "",
      fat: "",
      muscles: "",
      bmi: "",
      bmr: "",
      bodyAge: "",
      visceralFat: "",
    });
  };

  const handleCloseDailyRecordModal = () => {
    setShowDailyRecordModal(false);
    setSelectedUser(null);
  };

  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      await deleteUser(userToDelete.id);
      toast.success("User deleted successfully!");
      handleCloseDeleteModal();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
      console.error("Error deleting user:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDailyRecordChange = (field, value) => {
    setDailyRecordForm({ ...dailyRecordForm, [field]: value });
  };

  const handleSaveDailyRecord = async () => {
    try {
      if (!dailyRecordForm.weight) {
        toast.error("Weight is required");
        return;
      }

      setSaving(true);

      const record = {
        date: dailyRecordForm.date,
        weight: parseFloat(dailyRecordForm.weight) || 0,
        fat: parseInt(dailyRecordForm.fat) || 0,
        muscles: parseInt(dailyRecordForm.muscles) || 0,
        bmi: parseInt(dailyRecordForm.bmi) || 0,
        bmr: parseInt(dailyRecordForm.bmr) || 0,
        bodyAge: parseInt(dailyRecordForm.bodyAge) || 0,
        visceralFat: parseInt(dailyRecordForm.visceralFat) || 0,
      };

      const existingRecords = selectedUser.dailyRecords?.records || [];
      const updatedRecords = [...existingRecords, record];

      const totalWeight = updatedRecords.reduce(
        (sum, r) => sum + (parseFloat(r.weight) || 0),
        0
      );
      const average = (totalWeight / updatedRecords.length).toFixed(2);

      const updateData = {
        dailyRecords: {
          records: updatedRecords,
          average: average,
        },
      };

      await updateUserDetails(selectedUser.id, updateData);
      toast.success("Daily record added successfully!");
      handleCloseDailyRecordModal();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to add daily record");
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  // Unique option lists for filter dropdowns
  const referredByOptions = [...new Set(users.map(u => u.accountSecurity?.referredBy).filter(Boolean))];
  const sponsoredByOptions = [...new Set(users.map(u => u.accountSecurity?.sponsoredBy).filter(Boolean))];

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    const name = (user.personalInformation?.fullName || user.personalDetails?.name || user.displayName || "").toLowerCase();
    const email = (user.personalInformation?.email || user.personalDetails?.email || user.email || "").toLowerCase();
    const mobile = (user.personalInformation?.mobileNumber || user.personalDetails?.mobile || user.mobileNumber || "").toLowerCase();
    const id = (user.id || "").toLowerCase();

    const matchesSearch = !searchTerm || (
      name.includes(searchLower) ||
      email.includes(searchLower) ||
      mobile.includes(searchLower) ||
      id.includes(searchLower)
    );
    const matchesReferredBy = !filterReferredBy || (user.accountSecurity?.referredBy || "") === filterReferredBy;
    const matchesSponsoredBy = !filterSponsoredBy || (user.accountSecurity?.sponsoredBy || "") === filterSponsoredBy;

    return matchesSearch && matchesReferredBy && matchesSponsoredBy;
  });

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleSearchChange = (val) => { setSearchTerm(val); setCurrentPage(1); };
  const handleReferredByChange = (val) => { setFilterReferredBy(val); setCurrentPage(1); };
  const handleSponsoredByChange = (val) => { setFilterSponsoredBy(val); setCurrentPage(1); };
  console.log(users, "users")
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
                <p className="text-sm text-gray-500">Manage and view all registered users</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Total Users: <span className="font-semibold text-gray-900">{users.length}</span>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header with Search and Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Users</h2>
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
                  />
                </div>

                {/* Referred By Filter */}
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={filterReferredBy}
                    onChange={(e) => handleReferredByChange(e.target.value)}
                    className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-gray-700"
                  >
                    <option value="">All Referred By</option>
                    {referredByOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* Sponsored By Filter */}
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={filterSponsoredBy}
                    onChange={(e) => handleSponsoredByChange(e.target.value)}
                    className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-gray-700"
                  >
                    <option value="">All Sponsored By</option>
                    {sponsoredByOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* Clear Filters */}
                {(filterReferredBy || filterSponsoredBy) && (
                  <button
                    onClick={() => { handleReferredByChange(""); handleSponsoredByChange(""); }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referred By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shakes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-500">Loading users...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => {
                    const name = user.personalInformation?.fullName || user.personalDetails?.name || user.displayName || "N/A";
                    const email = user.personalInformation?.email || user.personalDetails?.email || user.email || "N/A";
                    const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || user.mobileNumber || "-";
                    const role = user.accountSecurity?.accountRole || user.profile?.role || user.role || "user";
                    const photoURL = user.profile?.photoURL || user.photoURL;
                    const initials = name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);
                    const joinedDate = user.metadata?.createdAt || user.createdAt
                      ? new Date(user.metadata?.createdAt || user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                      : "-";

                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleSelectUser(user.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {photoURL ? (
                              <img
                                src={photoURL}
                                alt={name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center font-semibold text-xs">
                                {initials}
                              </div>
                            )}
                            <span className="text-sm font-medium text-gray-900">{name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{mobile}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{joinedDate}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {user.accountSecurity?.referredBy || "-"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              {user.inventory?.remainingShakes ?? 0}
                            </span>
                            <button
                              onClick={() => handleOpenShakesModal(user)}
                              className="p-1 text-blue-500 hover:bg-blue-50 rounded-md transition-colors"
                              title="Edit Remaining Shakes"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/user-details/${user.id}`)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Profile"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenDailyRecordModal(user)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Add Daily Record"
                            >
                              <Activity className="w-4 h-4" />
                            </button>
                            {(currentUser?.role === "admin" || currentUser?.profile?.role === "admin" || currentUser?.role === "superadmin" || currentUser?.profile?.role === "superadmin") && (
                              <>
                                <button
                                  onClick={() => navigate(`/admin/edit-user/${user.id}`)}
                                  className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                  title="Edit User"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteModal(user)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete User"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>–<span className="font-semibold">{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)}</span> of <span className="font-semibold">{filteredUsers.length}</span> users
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === "..." ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${currentPage === p
                          ? "bg-blue-600 text-white border-blue-600 font-semibold"
                          : "border-gray-300 hover:bg-gray-50 text-gray-700"
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )
                }
                <button
                  onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Delete User</h2>
              </div>
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {userToDelete && (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Are you sure you want to delete this user? This action cannot be undone.
                  </p>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Name:</span>{" "}
                      {userToDelete.personalInformation?.fullName || userToDelete.personalDetails?.name || userToDelete.displayName || "N/A"}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-semibold">Email:</span>{" "}
                      {userToDelete.personalInformation?.email || userToDelete.personalDetails?.email || userToDelete.email || "N/A"}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-semibold">User ID:</span> #{userToDelete.id.slice(0, 8)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Record Modal */}
      {showDailyRecordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Add Daily Record</h2>
              </div>
              <button
                onClick={handleCloseDailyRecordModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {selectedUser && (
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">User:</span>{" "}
                    {selectedUser.personalInformation?.fullName || selectedUser.personalDetails?.name || selectedUser.displayName || "N/A"}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={dailyRecordForm.date}
                    onChange={(e) => handleDailyRecordChange("date", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (kg) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={dailyRecordForm.weight}
                    onChange={(e) => handleDailyRecordChange("weight", e.target.value)}
                    placeholder="e.g., 70.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fat</label>
                  <input
                    type="number"
                    value={dailyRecordForm.fat}
                    onChange={(e) => handleDailyRecordChange("fat", e.target.value)}
                    placeholder="e.g., 25"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Muscles
                  </label>
                  <input
                    type="number"
                    value={dailyRecordForm.muscles}
                    onChange={(e) => handleDailyRecordChange("muscles", e.target.value)}
                    placeholder="e.g., 45"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BMI</label>
                  <input
                    type="number"
                    value={dailyRecordForm.bmi}
                    onChange={(e) => handleDailyRecordChange("bmi", e.target.value)}
                    placeholder="e.g., 24"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BMR</label>
                  <input
                    type="number"
                    value={dailyRecordForm.bmr}
                    onChange={(e) => handleDailyRecordChange("bmr", e.target.value)}
                    placeholder="e.g., 1500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body Age
                  </label>
                  <input
                    type="number"
                    value={dailyRecordForm.bodyAge}
                    onChange={(e) => handleDailyRecordChange("bodyAge", e.target.value)}
                    placeholder="e.g., 30"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visceral Fat
                  </label>
                  <input
                    type="number"
                    value={dailyRecordForm.visceralFat}
                    onChange={(e) => handleDailyRecordChange("visceralFat", e.target.value)}
                    placeholder="e.g., 8"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleSaveDailyRecord}
                disabled={saving || !dailyRecordForm.weight}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Record"}
              </button>
              <button
                onClick={handleCloseDailyRecordModal}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Remaining Shakes Modal */}
      {showShakesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <Pencil className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Edit Remaining Shakes</h2>
                  {shakeEditUser && (
                    <p className="text-xs text-gray-500 truncate max-w-[180px]">
                      {shakeEditUser.personalInformation?.fullName || shakeEditUser.displayName || "User"}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleCloseShakesModal}
                disabled={savingShakes}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Remaining Shakes Quantity
              </label>
              <input
                type="number"
                min="0"
                value={shakeEditValue}
                onChange={(e) => setShakeEditValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveShakes()}
                placeholder="Enter quantity"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
              />
              <p className="mt-1.5 text-xs text-gray-400">Enter 0 or more shakes to assign to this user.</p>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                onClick={handleCloseShakesModal}
                disabled={savingShakes}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveShakes}
                disabled={savingShakes || shakeEditValue === ""}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                <Save className="w-4 h-4" />
                {savingShakes ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
